// src/pages/Dashboard.jsx
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../context/SnackbarContext";
import { jwtDecode } from "jwt-decode";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import QrCode from "../components/patients/modals/QrCode";
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { getPetOwnerById } from "../api/petOwnerService";
import PetOwnerView from "../components/patients/modals/PetOwnerView";
import PetOwnerForm from "../components/patients/forms/PetOwnerForm";
import UpcomingAppointmentsList from "../components/history/lists/UpcomingAppointmentsList";
import LowStockExpiringInventoryItemsList from "../components/inventory/lists/LowStockExpiringInventoryItemsList";
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import { useNavigate } from "react-router-dom";
import AppointmentsCalendar from "../components/history/display/AppointmentsCalendar";
import AppointmentsCalendarDesktop from "../components/history/display/AppointmentsCalendarDesktop";
import RequirePermission from "../components/RequirePermission";
import { SUB_MODULES } from "../constants/subModulesConstants";
import InviteNotification from "../components/users/modals/InviteNotification";
import OfficeSwitcher from "../components/settings/modals/OfficeSwitcher";
import RecommendedVisitsList from "../components/history/lists/RecommendedVisitsList";

const Dashboard = () => {
    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();

    const baseApiUrl = process.env.REACT_APP_API_URL;
    const publicUploadsFolder = "public_uploads";
    const token = localStorage.getItem("accessToken");
    const officeId = jwtDecode(token).active_office_id;

    const [petOwnerFormOpen, setPetOwnerFormOpen] = React.useState(false);
    const [petFormMode, setPetFormMode] = React.useState('create');
    const [selectedPetOwner, setSelectedPetOwner] = React.useState(null);
    const [petOwnerViewOpen, setPetOwnerViewOpen] = React.useState(false);

    const handleAdd = () => {
        setSelectedPetOwner(null);
        setPetFormMode("create");
        setPetOwnerFormOpen(true);
    };

    const handleEdit = () => {
        setPetFormMode("edit");
        setPetOwnerFormOpen(true);
    };

    const onSavePetOwnerSuccess = async (savedPetOwner) => {
        const updatedPetOwner = await getPetOwnerById(savedPetOwner.id);
        setSelectedPetOwner(updatedPetOwner);
        if (!petOwnerViewOpen) setPetOwnerViewOpen(true);
    };

    const navigate = useNavigate();
    const handleMedicalHistoryClick = () => {
        navigate("/history");
    };

    // ✅ Detect desktop viewport (lg and up)
    const isDesktop = useMediaQuery('(min-width:1024px)');

    const [appointmentsRefreshKey, setAppointmentsRefreshKey] = React.useState(0);
    const refreshUpcomingAppointments = () => {
      // Increment the key to force re-mount
      setAppointmentsRefreshKey(prev => prev + 1);
    };

    return (
        <>
        <InviteNotification />
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ flexGrow: 1, marginBottom: "0rem" }}>
              {localStorage.getItem('activeOfficeName')}
          </Typography>
          <OfficeSwitcher />
          <Box
              component="img"
              src={`${baseApiUrl}/${publicUploadsFolder}/logo-${officeId}.png`}
              alt="logo"
              sx={{
                  width: "100%",
                  maxWidth: 500,
                  display: "block",
                  margin: "0 auto",
              }}
              onError={(e) => {
                  e.currentTarget.src = "/images/logo-0.png";
              }}
          />
        
          <List>

              <ListItem>
                <ListItemButton onClick={handleMedicalHistoryClick}>
                  <ListItemIcon>
                      <ContentPasteSearchIcon fontSize='large' color='secondary' />
                  </ListItemIcon>
                  <ListItemText 
                      primary="Patient Medical History"
                      secondary="search patient medical history"
                  />
                </ListItemButton>
              </ListItem>
              <RequirePermission
                subModuleName={SUB_MODULES.QR_CODE}
                minAccessLevel={2}
              >
                <QrCode />
              </RequirePermission>
              <RequirePermission
                subModuleName={SUB_MODULES.PET_OWNERS}
                minAccessLevel={2}
              >
                <ListItem>
                    <ListItemButton onClick={handleAdd}>
                    <ListItemIcon>
                        <PersonAddAlt1Icon fontSize='large' color='secondary' />
                    </ListItemIcon>
                    <ListItemText 
                        primary="New Patient"
                        secondary="create new pet owner and pet(s) record"
                    />
                    </ListItemButton>
                </ListItem>
              </RequirePermission>
          </List>
          
          <RequirePermission
            subModuleName={SUB_MODULES.APPOINTMENT}
            minAccessLevel={1}
          >
            {isDesktop ? <AppointmentsCalendarDesktop key={appointmentsRefreshKey} /> : <AppointmentsCalendar key={appointmentsRefreshKey} />}
          </RequirePermission>
          <RequirePermission
            subModuleName={SUB_MODULES.APPOINTMENT}
            minAccessLevel={1}
          >
            <UpcomingAppointmentsList 
              key={appointmentsRefreshKey} 
              mode='dashboard' 
            />
          </RequirePermission>
          <RequirePermission
            subModuleName={SUB_MODULES.APPOINTMENT}
            minAccessLevel={1}
          >
            <RecommendedVisitsList
              refreshUpcomingAppointments={refreshUpcomingAppointments}
              mode='dashboard' />
          </RequirePermission>
          <RequirePermission
            subModuleName={SUB_MODULES.ITEMS}
            minAccessLevel={1}
          >
            <LowStockExpiringInventoryItemsList mode='dashboard' />
          </RequirePermission>
        </Stack>

        <PetOwnerView 
            handleEdit={handleEdit}
            setPetOwners={null}
            petOwner={selectedPetOwner}
            open={petOwnerViewOpen}
            onClose={() => setPetOwnerViewOpen(false)}
        />
        <PetOwnerForm 
            successCallback={onSavePetOwnerSuccess}
            setPetOwners={null} 
            petOwner={selectedPetOwner}
            mode={petFormMode}
            open={petOwnerFormOpen}
            onClose={() => setPetOwnerFormOpen(false)}
        />
        </>
    );
};

export default Dashboard;
